import type { GanttRoutePointAvailableActionsDto } from '@/modules/shared/dto/gantt-route-point-available-actions.dto.ts';
import type {
  GanttRoutePointActionAvailability,
  GanttRoutePointAvailableActions,
} from '@/modules/shared/types/gantt-route-point-available-actions.type.ts';

const INVALID_RESPONSE_ERROR = 'Invalid Gantt route point available actions response.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isAvailability(value: unknown): value is GanttRoutePointAvailableActionsDto['items']['ACTIONS'][string] {
  return (
    isRecord(value) &&
    typeof value.VISIBLE === 'boolean' &&
    (typeof value.ENABLED === 'boolean' || value.ENABLED === null)
  );
}

export class GanttRoutePointAvailableActionsAdapter {
  static map(response: unknown, requestedRoutePointId: number): GanttRoutePointAvailableActions {
    if (!isRecord(response) || !isRecord(response.items)) {
      throw new Error(INVALID_RESPONSE_ERROR);
    }

    const { RP_ID: routePointId, DOC_ID: documentId, ACTIONS: actionsDto } = response.items;

    if (typeof routePointId !== 'number' || typeof documentId !== 'number' || !isRecord(actionsDto)) {
      throw new Error(INVALID_RESPONSE_ERROR);
    }

    if (routePointId !== requestedRoutePointId) {
      throw new Error(
        `Available actions response route point ${routePointId} does not match requested route point ${requestedRoutePointId}.`
      );
    }

    const actions: Record<string, GanttRoutePointActionAvailability> = {};

    for (const [key, availability] of Object.entries(actionsDto)) {
      if (!isAvailability(availability)) {
        throw new Error(INVALID_RESPONSE_ERROR);
      }

      actions[key] = {
        visible: availability.VISIBLE,
        enabled: availability.ENABLED ?? false,
      };
    }

    return { routePointId, documentId, actions };
  }
}
